import { DataGridPro, DataGridProProps } from "@mui/x-data-grid-pro";
import { styled } from "@mui/material/styles";

interface DataGridUIProps extends DataGridProProps {
  isInAIAvatarLayout?: boolean;
}

const DataGridUI = styled(DataGridPro)<DataGridUIProps>(
  ({ theme, isInAIAvatarLayout }) => {
    // Avatar dark: primary is near-white (inverted brand) — use secondary so header stays dark.
    // Avatar light / other layouts: primary header with contrast text.
    const headerBackground = isInAIAvatarLayout && theme.palette.mode === 'dark'
      ? theme.palette.secondary.main
      : theme.palette.primary.main;
    const headerTextColor = isInAIAvatarLayout && theme.palette.mode === 'dark'
      ? theme.palette.secondary.contrastText
      : theme.palette.primary.contrastText;

    return {
      // New row highlighting
      "& .super-app-theme--New": {
        backgroundColor: theme.palette.action.selected,
        "&:hover": {
          backgroundColor: `${theme.palette.action.hover} !important`,
        },
      },
      // Column Headers
      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: headerBackground,
        color: headerTextColor,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        color: headerTextColor,
      },
      "& .MuiDataGrid-columnHeader .MuiCheckbox-root svg": {
        color: headerTextColor,
      },
      "& .MuiDataGrid-menuIconButton": {
        color: headerTextColor,
      },
      "& .MuiDataGrid-columnHeaderTitleContainer .MuiDataGrid-sortIcon": {
        color: headerTextColor,
      },
    // Pinned columns — match main header primary styling on pinned header rows
    "& .MuiDataGrid-pinnedColumnHeaders--left": {
      backgroundColor: headerBackground,
      color: headerTextColor,
    },
    "& .MuiDataGrid-pinnedColumnHeaders--left .MuiDataGrid-columnHeader": {
      backgroundColor: headerBackground,
      color: headerTextColor,
    },
    "& .MuiDataGrid-pinnedColumnHeaders--right": {
      backgroundColor: headerBackground,
      color: headerTextColor,
      boxShadow: theme.shadows[4],
    },
    "& .MuiDataGrid-pinnedColumnHeaders--right .MuiDataGrid-columnHeader": {
      backgroundColor: headerBackground,
      color: headerTextColor,
    },
    "& .MuiDataGrid-pinnedColumnHeaders--right .MuiDataGrid-columnHeaderTitle": {
      color: headerTextColor,
    },
    "& .MuiDataGrid-pinnedColumnHeaders--right .MuiDataGrid-menuIconButton": {
      color: headerTextColor,
    },
    "& .MuiDataGrid-pinnedColumnHeaders--right .MuiDataGrid-sortIcon": {
      color: headerTextColor,
    },
    "& .MuiDataGrid-pinnedColumns--right": {
      boxShadow: theme.shadows[2],
    },
    "& .MuiDataGrid-pinnedColumns--right .MuiDataGrid-cell": {
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiDataGrid-row:hover .MuiDataGrid-pinnedColumns--right .MuiDataGrid-cell": {
      backgroundColor: theme.palette.action.hover,
    },
    // Row hover effect
    "& .MuiDataGrid-row:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    // Cell borders and styling
    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-cell:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-cell:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-cell:focus-within": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    // Grid background
    backgroundColor: theme.palette.background.paper,
    // Footer
    "& .MuiDataGrid-footerContainer": {
      backgroundColor: theme.palette.background.paper,
      borderTop: `1px solid ${theme.palette.divider}`,
    },

    // Remove ALL focus borders and outlines from the entire DataGrid
    "&:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "&:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "&:focus-within": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    
    // Remove focus from the root grid element
    "& .MuiDataGrid-root": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-root:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-root:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-root:focus-within": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    
    // Remove focus from the main grid container
    "& .MuiDataGrid-main": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-main:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-main:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    
    
    // Remove focus from column headers
    "& .MuiDataGrid-columnHeader": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-columnHeader:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-columnHeader:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-columnHeader:focus-within": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    
    // Remove focus from rows
    "& .MuiDataGrid-row": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-row:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-row:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-row:focus-within": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    
    // Remove focus from virtual scroller
    "& .MuiDataGrid-virtualScroller": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-virtualScroller:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-virtualScroller:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    
    // Remove focus from any other grid elements
    "& *:focus": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& *:focus-visible": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    "& *:focus-within": {
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
    },
    };
  }
);

export default DataGridUI;
